// Admin-palette configuration of the shared command palette (command_palette.mjs). The
// generic ranking, cache, and row markup live there; this module only pins the admin
// palette's own types.
import { MAX_RESULTS, rankCommandResults } from "./command_palette.mjs";

export {
  EMPTY_PREFIX_MIN_LENGTH,
  MAX_RESULTS,
  MIN_REMOTE_QUERY_LENGTH,
  NavigationRecordCache,
  REMOTE_SEARCH_DEBOUNCE_MS,
  commandGroupHeader,
  commandResultRow,
  escapeHtml,
  highlightMatch,
  isCommandSearchShortcut,
  normalizeTransientNavigationState,
  platformModifierLabel,
} from "./command_palette.mjs";

// Admin pages and partners ship with the layout and have no server pass, so they must
// match locally; pages are also the only thing listed before anything is typed. Records
// (claims, bills, members, users) always come from the endpoint.
const LOCAL_TYPES = ["page", "partner"];
const BLANK_QUERY_TYPES = ["page"];
// Pages first (the palette's navigation job), then partners (switching partners from
// admin), then records. Members lead the records: a name or phone match is usually a
// person support is trying to find, and their claims follow underneath.
const TYPE_ORDER = ["page", "partner", "member", "claim", "bill", "user"];

// The partner palette short-circuits any query whose cached prefix matched nothing,
// because every one of its dimensions is a monotonic substring match. The admin search is
// not monotonic: internal ids and guarantor ids match exactly, so `GRT-555` can return
// nothing while `GRT-55512` returns a bill (and an id like `12` vs `1234` likewise). Only
// an exact cache hit is reused here.
export function cachedAdminRecords(cache, query) {
  return cache.get(query) || null;
}

// With ~90 pages and generous keywords, a short query can match more pages than the
// whole result budget, which would silently drop every record the endpoint just found.
// Pages are capped at PAGE_CAP whenever records are on screen; with no records they may
// use the full budget.
export const PAGE_CAP = 6;

export function rankAdminCommandResults(pages, records, query, { recordsAreCurrent = true } = {}) {
  const options = {
    recordsAreCurrent,
    localTypes: LOCAL_TYPES,
    blankQueryTypes: BLANK_QUERY_TYPES,
    typeOrder: TYPE_ORDER,
  };
  // Untruncated here: exact-id records must be pulled out before any budget applies.
  const rankedRecords = rankCommandResults([], records, query, { ...options, maxResults: Number.POSITIVE_INFINITY });
  const pageBudget = rankedRecords.length ? Math.min(PAGE_CAP, MAX_RESULTS) : MAX_RESULTS;
  const rankedPages = rankCommandResults(pages, [], query, { ...options, maxResults: pageBudget });

  // A record whose internal id is exactly the query (the server flags it `exact`) is what
  // the admin pasted, so it leads the list and is never cut by the budget: reserve its
  // slots before pages and other records fill the rest.
  const exact = rankedRecords.filter((record) => record.exact);
  const others = rankedRecords.filter((record) => !record.exact);
  const pageSlots = Math.max(0, Math.min(rankedPages.length, MAX_RESULTS - exact.length));
  const keptPages = rankedPages.slice(0, pageSlots);
  const otherSlots = Math.max(0, MAX_RESULTS - exact.length - keptPages.length);

  // Regrouped by type (first appearance wins) so an exact claim and the other claims share
  // one "Claims" heading instead of the dialog rendering it twice.
  return groupByFirstAppearance([...exact, ...keptPages, ...others.slice(0, otherSlots)]);
}

function groupByFirstAppearance(results) {
  const groups = new Map();
  results.forEach((result) => {
    const group = groups.get(result.type);
    group ? group.push(result) : groups.set(result.type, [result]);
  });
  return [...groups.values()].flat();
}
